import React from "react";
import {
  Check,
  ShieldAlert,
  Users,
  BookOpen,
  Layers,
  Bird,
  Gavel,
  Trees,
  Wrench,
  Download,
  ArrowRight,
  WifiOff,
  Bell,
  MapPin,
  Languages,
  Fingerprint,
  Building2,
  Stamp,
} from "lucide-react";
import { t } from "@/i18n/translations";

const RangerBookIllustration = () => (
  <div className="relative w-full max-w-[480px] aspect-[4/3] rounded-3xl bg-[#FDF5EB] border-[1.5px] border-[#242220] p-5 overflow-hidden shadow-[8px_8px_0_0_#242220]">
    <div className="absolute inset-5 rounded-2xl bg-white border-[1.5px] border-[#242220] flex">
      <div className="w-3 bg-[#FBD556] border-r-[1.5px] border-[#242220] flex flex-col items-center justify-center gap-3">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="w-1.5 h-1.5 rounded-full bg-[#242220]" />
        ))}
      </div>
      <div className="flex-1 p-4 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-[#242220]/60">Szolgálati Napló</div>
            <div className="text-[13px] font-bold text-[#242220]">2025 · 12 · 18</div>
          </div>
          <div className="px-2.5 py-0.5 rounded-full bg-[#A1E1B9] text-[9px] font-bold text-[#242220] border-[1px] border-[#242220]">27 ENTRIES</div>
        </div>
        <div className="space-y-2 mb-3 text-[10px] font-semibold text-[#242220]">
          {[
            { dot: "#FBD556", t: "07:12", l: "Patrol — Aggtelek N." },
            { dot: "#FF9456", t: "09:38", l: "Inspection — hunting" },
            { dot: "#A1E1B9", t: "11:04", l: "Biotics — fox sighting" },
            { dot: "#FBD556", t: "13:21", l: "Camera retrieval" },
            { dot: "#FF9456", t: "15:47", l: "Rescue — injured deer" },
          ].map((row, i) => (
            <div key={i} className="flex items-center gap-2.5">
              <span className="text-[9px] font-mono text-[#242220]/50 w-8">{row.t}</span>
              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: row.dot }} />
              <span className="truncate">{row.l}</span>
            </div>
          ))}
        </div>
        <div className="mt-auto flex items-center justify-between pt-3 border-t border-[#242220]/15">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg border-[1.5px] border-[#242220] bg-[#FBD556]/30 flex items-center justify-center">
              <Fingerprint size={16} className="text-[#242220]" strokeWidth={1.8} />
            </div>
            <div className="leading-tight">
              <div className="text-[8px] font-bold uppercase tracking-wider text-[#242220]/60">Finalised</div>
              <div className="text-[10px] font-bold text-[#242220]">Biometric ✓</div>
            </div>
          </div>
          <div className="px-2 py-1 rounded-md bg-[#242220] text-[#FDF5EB] text-[9px] font-bold tracking-wider">PDF</div>
        </div>
      </div>
    </div>
    <div className="absolute top-2 right-2 px-2.5 py-1 rounded-full bg-[#1E1E1E] text-[#FBD556] text-[9px] font-bold tracking-wider uppercase border-[1.5px] border-[#FBD556]">Audit Trail</div>
  </div>
);

const SharedServiceIllustration = () => (
  <div className="relative w-full max-w-[480px] aspect-[4/3] rounded-3xl bg-[#1E1E1E] border-[1.5px] border-[#FDF5EB]/15 p-5 overflow-hidden shadow-[8px_8px_0_0_#242220]">
    <svg viewBox="0 0 480 360" className="w-full h-full" role="img" aria-label="Shared service team map">
      <g stroke="#FDF5EB" strokeOpacity="0.08" strokeWidth="1">
        {Array.from({ length: 9 }).map((_, i) => (
          <React.Fragment key={i}>
            <line x1={i * 60} y1="0" x2={i * 60} y2="360" />
            <line x1="0" y1={i * 45} x2="480" y2={i * 45} />
          </React.Fragment>
        ))}
      </g>
      <path d="M40 80 L 160 60 L 240 120 L 200 200 L 80 220 Z" fill="#A1E1B9" fillOpacity="0.12" stroke="#A1E1B9" strokeOpacity="0.35" strokeWidth="1.2" />
      <path d="M260 140 L 400 100 L 440 220 L 340 280 L 250 240 Z" fill="#A1E1B9" fillOpacity="0.1" stroke="#A1E1B9" strokeOpacity="0.35" strokeWidth="1.2" />
      <path d="M70 240 Q 130 200, 160 170 T 240 150" stroke="#FBD556" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M380 280 Q 340 220, 300 200 T 270 170" stroke="#FF9456" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M120 100 Q 170 130, 200 160 T 250 200" stroke="#A1E1B9" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      {[
        { x: 240, y: 150, c: "#FBD556", name: "Áron" },
        { x: 270, y: 170, c: "#FF9456", name: "Béla" },
        { x: 250, y: 200, c: "#A1E1B9", name: "Csaba" },
      ].map((r) => (
        <g key={r.name} transform={`translate(${r.x} ${r.y})`}>
          <circle r="14" fill={r.c} fillOpacity="0.18">
            <animate attributeName="r" values="10;22;10" dur="2.4s" repeatCount="indefinite" />
            <animate attributeName="fill-opacity" values="0.4;0;0.4" dur="2.4s" repeatCount="indefinite" />
          </circle>
          <circle r="7" fill={r.c} stroke="#1E1E1E" strokeWidth="2" />
          <text y="-14" textAnchor="middle" fontSize="10" fontWeight="700" fontFamily="Quicksand" fill={r.c}>{r.name}</text>
        </g>
      ))}
      <g transform="translate(20 20)">
        <rect width="120" height="26" rx="13" fill="#FDF5EB" />
        <circle cx="14" cy="13" r="4" fill="#A1E1B9">
          <animate attributeName="fill-opacity" values="0.4;1;0.4" dur="1.2s" repeatCount="indefinite" />
        </circle>
        <text x="26" y="17" fontSize="10" fontWeight="700" fontFamily="Quicksand" fill="#242220">MQTT · live sync</text>
      </g>
      <g transform="translate(340 20)">
        <rect width="118" height="26" rx="13" fill="#FBD556" />
        <text x="59" y="17" textAnchor="middle" fontSize="10" fontWeight="700" fontFamily="Quicksand" fill="#242220">MISSION · ACTIVE</text>
      </g>
    </svg>
  </div>
);

const SOSIllustration = () => (
  <div className="relative w-full max-w-[480px] aspect-[4/3] rounded-3xl bg-[#1E1E1E] border-[1.5px] border-[#FDF5EB]/15 p-5 overflow-hidden shadow-[8px_8px_0_0_#242220] flex items-center justify-center">
    <svg viewBox="0 0 480 360" className="absolute inset-0 w-full h-full" aria-hidden="true">
      <g stroke="#FDF5EB" strokeOpacity="0.05" strokeWidth="1">
        {Array.from({ length: 9 }).map((_, i) => (
          <React.Fragment key={i}>
            <line x1={i * 60} y1="0" x2={i * 60} y2="360" />
            <line x1="0" y1={i * 45} x2="480" y2={i * 45} />
          </React.Fragment>
        ))}
      </g>
      {[0, 1, 2].map((i) => (
        <circle key={i} cx="240" cy="180" r="40" fill="none" stroke="#FF4D4D" strokeWidth="2" opacity="0.6">
          <animate attributeName="r" values="40;160;40" dur="3s" begin={`${i * 1}s`} repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.6;0;0.6" dur="3s" begin={`${i * 1}s`} repeatCount="indefinite" />
        </circle>
      ))}
      {[{ x: 90, y: 90 }, { x: 380, y: 100 }, { x: 80, y: 270 }, { x: 400, y: 280 }].map((r, i) => (
        <g key={i} transform={`translate(${r.x} ${r.y})`}>
          <circle r="9" fill="#2AC4D9" fillOpacity="0.2" />
          <circle r="6" fill="#2AC4D9" stroke="#1E1E1E" strokeWidth="2" />
          <text y="-10" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#2AC4D9">R{i + 1}</text>
        </g>
      ))}
      {[{ x: 90, y: 90 }, { x: 380, y: 100 }, { x: 80, y: 270 }, { x: 400, y: 280 }].map((r, i) => (
        <line key={i} x1={r.x} y1={r.y} x2="240" y2="180" stroke="#2AC4D9" strokeWidth="1.5" strokeDasharray="4 4" opacity="0.4" />
      ))}
    </svg>
    <div className="relative z-10 w-32 h-32 rounded-full bg-[#FF4D4D] border-[4px] border-[#FDF5EB] flex flex-col items-center justify-center shadow-[0_0_60px_rgba(255,77,77,0.5)]">
      <ShieldAlert size={28} className="text-[#FDF5EB] mb-1" strokeWidth={2.2} />
      <div className="text-[#FDF5EB] font-extrabold text-2xl tracking-widest leading-none">SOS</div>
      <div className="text-[#FDF5EB]/80 text-[8px] font-bold tracking-[0.2em] uppercase mt-0.5">Active</div>
    </div>
    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-[#FDF5EB] text-[#242220] text-[10px] font-bold tracking-wider uppercase border-[1.5px] border-[#FF4D4D] z-10 whitespace-nowrap">
      <span className="text-[#FF4D4D]">●</span> 4 supporters en route
    </div>
  </div>
);

const LegalDocumentsIllustration = () => (
  <div className="relative w-full max-w-[480px] aspect-[4/3] rounded-3xl bg-[#FDF5EB] border-[1.5px] border-[#242220] p-5 overflow-hidden shadow-[8px_8px_0_0_#242220]">
    <svg viewBox="0 0 480 360" className="w-full h-full" role="img" aria-label="Legal document AI processing">
      <g stroke="#242220" strokeOpacity="0.06" strokeWidth="1">
        {Array.from({ length: 9 }).map((_, i) => (
          <React.Fragment key={i}>
            <line x1={i * 60} y1="0" x2={i * 60} y2="360" />
            <line x1="0" y1={i * 45} x2="480" y2={i * 45} />
          </React.Fragment>
        ))}
      </g>
      <g transform="translate(30 30)">
        <rect width="160" height="200" rx="10" fill="#FDF5EB" stroke="#242220" strokeWidth="1.5" />
        <rect x="16" y="16" width="128" height="168" rx="6" fill="#FDF5EB" stroke="#242220" strokeWidth="1" strokeDasharray="3 3" />
        <text x="80" y="44" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#242220">PDF SCAN</text>
        <text x="80" y="60" textAnchor="middle" fontSize="7" fontFamily="Quicksand" fill="#242220" opacity="0.6">Engedély · Permit</text>
        <line x1="28" y1="72" x2="132" y2="72" stroke="#242220" strokeWidth="1" opacity="0.3" />
        {Array.from({ length: 6 }).map((_, i) => (
          <rect key={i} x="28" y={80 + i * 14} width={100 - i * 10} height="6" rx="2" fill="#242220" opacity={0.15 - i * 0.02} />
        ))}
        <rect x="28" y="170" width="60" height="6" rx="2" fill="#A1E1B9" />
        <rect x="94" y="170" width="38" height="6" rx="2" fill="#FBD556" />
      </g>
      <g transform="translate(200 50)">
        <rect width="80" height="30" rx="8" fill="#FF9456" stroke="#242220" strokeWidth="1.5" />
        <text x="40" y="20" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#242220">Mistral OCR</text>
      </g>
      <path d="M190 110 L200 80" stroke="#242220" strokeWidth="1.5" markerEnd="url(#arrLD)" />
      <path d="M280 80 L300 120" stroke="#242220" strokeWidth="1.5" />
      <defs>
        <marker id="arrLD" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="4" markerHeight="4" orient="auto">
          <path d="M0,0 L8,5 L0,10 z" fill="#242220" />
        </marker>
      </defs>
      <g transform="translate(300 40)">
        <rect width="150" height="50" rx="8" fill="#1E1E1E" />
        <text x="75" y="20" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#A1E1B9">LLM EXTRACTS</text>
        <text x="75" y="36" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#FBD556">Authority · Date · Expiry</text>
      </g>
      <g transform="translate(300 110)">
        <rect width="150" height="50" rx="8" fill="#1E1E1E" />
        <text x="75" y="20" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#FBD556">QDRANT VECTOR INDEX</text>
        <text x="75" y="36" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#FDF5EB">Semantic search</text>
      </g>
      <g transform="translate(300 180)">
        <rect width="150" height="50" rx="8" fill="#1E1E1E" />
        <text x="75" y="20" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#A1E1B9">TANTIVY KEYWORD INDEX</text>
        <text x="75" y="36" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#FDF5EB">Full-text search</text>
      </g>
      <g transform="translate(300 250)">
        <rect width="150" height="50" rx="8" fill="#242220" stroke="#FBD556" strokeWidth="1.5" />
        <text x="75" y="22" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#FBD556">HYBRID SEARCH</text>
        <text x="75" y="38" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#FDF5EB">Score fusion</text>
      </g>
      <path d="M375 160 V 180" stroke="#FDF5EB" strokeWidth="1.5" opacity="0.5" />
      <path d="M375 230 V 250" stroke="#FDF5EB" strokeWidth="1.5" opacity="0.5" />
      <g transform="translate(60 250)">
        <rect width="100" height="28" rx="14" fill="#A1E1B9" stroke="#242220" strokeWidth="1.5" />
        <text x="50" y="18" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#242220">✓ Status: Valid</text>
      </g>
      <g transform="translate(60 286)">
        <rect width="100" height="28" rx="14" fill="#FF9456" stroke="#242220" strokeWidth="1.5" />
        <text x="50" y="18" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#242220">⚡ Expiring soon</text>
      </g>
      <g transform="translate(60 320)">
        <rect width="100" height="28" rx="14" fill="#FF4D4D" stroke="#242220" strokeWidth="1.5" />
        <text x="50" y="18" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#242220">✕ Expired</text>
      </g>
    </svg>
  </div>
);

const CommunicationIllustration = () => (
  <div className="relative w-full max-w-[480px] aspect-[4/3] rounded-3xl bg-[#1E1E1E] border-[1.5px] border-[#FDF5EB]/15 p-5 overflow-hidden shadow-[8px_8px_0_0_#242220]">
    <svg viewBox="0 0 480 360" className="w-full h-full" role="img" aria-label="Communication and collaboration">
      <g fill="none" stroke="#FDF5EB" strokeOpacity="0.06" strokeWidth="1">
        {Array.from({ length: 9 }).map((_, i) => (
          <React.Fragment key={i}>
            <line x1={i * 60} y1="0" x2={i * 60} y2="360" />
            <line x1="0" y1={i * 45} x2="480" y2={i * 45} />
          </React.Fragment>
        ))}
      </g>
      <g transform="translate(30 20)">
        <rect width="200" height="160" rx="12" fill="#1E1E1E" stroke="#FBD556" strokeWidth="1.5" strokeOpacity="0.5" />
        <text x="100" y="22" textAnchor="middle" fontSize="10" fontWeight="700" fontFamily="Quicksand" fill="#FBD556">CHAT · WebSocket</text>
        <line x1="16" y1="32" x2="184" y2="32" stroke="#FBD556" strokeWidth="1" opacity="0.3" />
        {[
          { m: "Patrol update: eagle nest OK", s: "Áron · 10:23", c: "#242220", t: "#FDF5EB" },
          { m: "Camera 14 needs battery", s: "Béla · 11:07", c: "#A1E1B9", t: "#242220" },
          { m: "On my way to Jósvafő", s: "Csaba · 11:14", c: "#FBD556", t: "#242220" },
        ].map((msg, i) => (
          <g key={i}>
            <rect x="16" y={42 + i * 38} width="168" height="30" rx="8" fill={msg.c} />
            <text x="26" y={56 + i * 38} fontSize="8" fontWeight="700" fontFamily="Quicksand" fill={msg.t}>{msg.m}</text>
            <text x="26" y={68 + i * 38} fontSize="7" fontFamily="Quicksand" fill={msg.t} opacity="0.6">{msg.s}</text>
          </g>
        ))}
      </g>
      <g transform="translate(250 20)">
        <rect width="200" height="160" rx="12" fill="#1E1E1E" stroke="#A1E1B9" strokeWidth="1.5" strokeOpacity="0.5" />
        <text x="100" y="22" textAnchor="middle" fontSize="10" fontWeight="700" fontFamily="Quicksand" fill="#A1E1B9">EMAIL · JMAP</text>
        <line x1="16" y1="32" x2="184" y2="32" stroke="#A1E1B9" strokeWidth="1" opacity="0.3" />
        {[
          { f: "Weekly patrol report", s: "ANPI HQ", r: true },
          { f: "Camera trap results", s: "Ranger Béla", r: false },
          { f: "Training reminder", s: "Athena LMS", r: true },
        ].map((e, i) => (
          <g key={i}>
            <rect x="16" y={42 + i * 38} width="168" height="30" rx="8" fill="#242220" stroke="#FDF5EB" strokeOpacity="0.1" strokeWidth="1" />
            <circle cx="26" cy={52 + i * 38} r="3" fill={e.r ? "#FBD556" : "transparent"} />
            <text x="36" y={56 + i * 38} fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#FDF5EB">{e.f}</text>
            <text x="36" y={68 + i * 38} fontSize="7" fontFamily="Quicksand" fill="#FDF5EB" opacity="0.5">{e.s}</text>
          </g>
        ))}
      </g>
      <g transform="translate(80 200)">
        <rect width="160" height="60" rx="10" fill="#242220" stroke="#FF9456" strokeWidth="1.5" strokeOpacity="0.5" />
        <text x="80" y="22" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#FF9456">PHONEBOOK</text>
        <text x="80" y="42" textAnchor="middle" fontSize="8" fontFamily="Quicksand" fill="#FDF5EB">47 contacts · searchable</text>
      </g>
      <g transform="translate(260 200)">
        <rect width="160" height="60" rx="10" fill="#242220" stroke="#FBD556" strokeWidth="1.5" strokeOpacity="0.5" />
        <text x="80" y="22" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#FBD556">MEETINGS</text>
        <text x="80" y="42" textAnchor="middle" fontSize="8" fontFamily="Quicksand" fill="#FDF5EB">Schedule · attend · minutes</text>
      </g>
      <g transform="translate(140 280)">
        <rect width="200" height="40" rx="20" fill="#A1E1B9" stroke="#242220" strokeWidth="1.5" />
        <text x="100" y="25" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#242220">File attachments · server expiry</text>
      </g>
    </svg>
  </div>
);

const GISIllustration = () => (
  <div className="relative w-full max-w-[480px] aspect-[4/3] rounded-3xl bg-[#FDF5EB] border-[1.5px] border-[#242220] p-5 overflow-hidden shadow-[8px_8px_0_0_#242220]">
    <svg viewBox="0 0 480 360" className="w-full h-full" role="img" aria-label="GIS layered map">
      {[{ y: 60, c: "#FBD556", label: "BIOTICS" }, { y: 110, c: "#A1E1B9", label: "PARCELS" }, { y: 160, c: "#FF9456", label: "TRAILS" }, { y: 210, c: "#FDF5EB", label: "BASE MAP" }].map((l, i) => (
        <g key={l.label}>
          <path d={`M ${80 + i * 4} ${l.y} L ${360 - i * 4} ${l.y - 18} L ${400 - i * 4} ${l.y + 22} L ${120 + i * 4} ${l.y + 40} Z`} fill={l.c} stroke="#242220" strokeWidth="1.5" fillOpacity="0.85" />
          <text x={`${260 - i * 4}`} y={`${l.y + 12}`} textAnchor="middle" fontSize="11" fontWeight="700" fontFamily="Quicksand" fill="#242220">{l.label}</text>
        </g>
      ))}
      <path d="M120 250 Q 180 240, 220 250 T 320 260 Q 360 268, 400 258" stroke="#FF4D4D" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeDasharray="5 4" />
      <circle cx="400" cy="258" r="6" fill="#FF4D4D" stroke="#242220" strokeWidth="1.5">
        <animate attributeName="r" values="6;9;6" dur="1.6s" repeatCount="indefinite" />
      </circle>
      {[{ x: 150, y: 250, c: "#A1E1B9" }, { x: 250, y: 252, c: "#FBD556" }, { x: 340, y: 260, c: "#FF9456" }].map((p, i) => (
        <g key={i} transform={`translate(${p.x - 6} ${p.y - 14})`}>
          <path d="M6 0 C 3 0, 0 3, 0 6 C 0 11, 6 16, 6 16 C 6 16, 12 11, 12 6 C 12 3, 9 0, 6 0 Z" fill={p.c} stroke="#242220" strokeWidth="1" />
        </g>
      ))}
      <g transform="translate(20 290)">
        <rect width="200" height="48" rx="10" fill="#242220" />
        <text x="10" y="18" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#A1E1B9" letterSpacing="1.4">WGS84</text>
        <text x="10" y="32" fontSize="11" fontFamily="Quicksand" fontWeight="700" fill="#FDF5EB">48.4742°N · 20.5025°E</text>
        <text x="10" y="44" fontSize="9" fontFamily="Quicksand" fontWeight="700" fill="#FBD556" letterSpacing="0.8">EOV  X: 753 421  Y: 312 998</text>
      </g>
      <g transform="translate(360 290)">
        <rect width="100" height="48" rx="10" fill="#FBD556" stroke="#242220" strokeWidth="1.5" />
        <circle cx="14" cy="24" r="6" fill="#242220" />
        <path d="M10 24 L13 27 L18 21" stroke="#FBD556" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <text x="26" y="20" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#242220">OFFLINE</text>
        <text x="26" y="32" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#242220">MBTiles</text>
      </g>
      <g transform="translate(20 18)">
        <rect width="148" height="22" rx="11" fill="#242220" />
        <text x="10" y="15" fontSize="10" fontWeight="700" fontFamily="Quicksand" fill="#FDF5EB">4 layers · MapLibre</text>
      </g>
    </svg>
  </div>
);

const DeepRow = ({ feature, index }) => {
  const reverse = index % 2 === 1;
  const Illustration = feature.Illustration;
  return (
    <section id={feature.id} data-testid={`deep-${feature.id}`} className="border-t-[1.5px] border-[#242220]/80">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 lg:py-28">
        <div className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${reverse ? "lg:[&>div:first-child]:order-2" : ""}`}>
          <div className="reveal">
            <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#FF9456] mb-4">{feature.eyebrow}</div>
            <h3 className="text-3xl sm:text-4xl lg:text-[2.4rem] font-bold tracking-tight text-[#FDF5EB] leading-[1.1] mb-4">{feature.headline}</h3>
            <div className="text-base sm:text-lg font-semibold text-[#FF9456] mb-6 leading-snug">{feature.sub}</div>
            <p className="text-[15px] text-[#FDF5EB]/70 leading-[1.75] mb-8 max-w-xl">{feature.body}</p>
            <ul className="space-y-3">
              {feature.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span aria-hidden="true" className="mt-0.5 w-5 h-5 rounded-full bg-[#A1E1B9]/15 border border-[#A1E1B9]/40 flex items-center justify-center shrink-0">
                    <Check size={12} className="text-[#A1E1B9]" strokeWidth={3} />
                  </span>
                  <span className="text-sm text-[#FDF5EB]/85 leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="reveal reveal-delay-2 flex justify-center lg:justify-end">
            <Illustration />
          </div>
        </div>
      </div>
    </section>
  );
};

const ModulesPhone = ({ lang }) => {
  const p = t(lang, "deepdive.modules.phone");
  const tiles = [
    { Icon: Bird, label: p.tiles[0], c: "#A1E1B9" },
    { Icon: Gavel, label: p.tiles[1], c: "#FF9456" },
    { Icon: Trees, label: p.tiles[2], c: "#FBD556" },
    { Icon: Wrench, label: p.tiles[3], c: "#A1E1B9" },
    { Icon: ShieldAlert, label: p.tiles[4], c: "#FF9456" },
    { Icon: BookOpen, label: p.tiles[5], c: "#FBD556" },
  ];

  return (
    <div className="relative max-w-[300px] mx-auto lg:mx-0 lg:ml-auto">
      <div className="absolute -inset-8 bg-[#A1E1B9] opacity-10 blur-3xl rounded-full" aria-hidden="true" />
      <div className="relative rounded-[38px] border-[9px] border-[#242220] bg-[#0f0f0f] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8),0_0_0_1px_rgba(251,213,86,0.15)] overflow-hidden float-device">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-[#242220] rounded-b-2xl z-20" />
        <div className="pt-11 px-4 pb-5">
          <div className="flex items-center justify-between mb-1 px-1">
            <span className="text-[10px] font-bold text-[#FDF5EB]/80">9:41</span>
            <span className="text-[8px] font-bold text-[#A1E1B9] tracking-[0.25em]">{p.moduleLabel}</span>
          </div>
          <div className="px-1 mb-4">
            <div className="text-[11px] font-bold text-[#FDF5EB]">{p.greeting}</div>
            <div className="text-[9px] text-[#FDF5EB]/50">{p.sub}</div>
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            {tiles.map(({ Icon, label, c }) => (
              <div key={label} className="rounded-2xl border border-white/8 bg-[#1E1E1E] p-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2" style={{ backgroundColor: `${c}1a` }}>
                  <Icon size={15} style={{ color: c }} strokeWidth={2.2} />
                </div>
                <div className="text-[9px] font-bold text-[#FDF5EB] leading-tight">{label}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center justify-between rounded-2xl border border-white/8 bg-[#1E1E1E] px-3 py-2.5">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#FF4D4D]/20 flex items-center justify-center text-[#FF4D4D] text-[9px] font-extrabold tracking-wider">SOS</span>
              <span className="text-[9px] font-bold text-[#FDF5EB]/80">{p.standby}</span>
            </div>
            <span className="text-[8px] text-[#A1E1B9] font-bold flex items-center gap-1">
              <WifiOff size={10} />
              {p.queue}
            </span>
          </div>
        </div>
      </div>
      <div className="absolute -top-5 -right-3 px-3 py-1.5 rounded-full bg-[#FBD556] text-[#242220] text-[10px] font-bold tracking-wider shadow-lg rotate-6">
        {p.badge}
      </div>
    </div>
  );
};

const ModuleGrid = ({ lang }) => {
  const groups = t(lang, "deepdive.modules.groups");
  const icons = [Bird, Gavel, Trees, Wrench, ShieldAlert];
  const accents = ["#A1E1B9", "#FF9456", "#FBD556", "#A1E1B9", "#FF9456"];

  return (
    <section id="modules" data-testid="section-modules" className="border-t-[1.5px] border-[#242220]/80 py-24 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl mb-14">
          <div className="reveal inline-flex items-center gap-2 mb-4">
            <span className="h-px w-8 bg-[#FBD556]" />
            <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#FBD556]">{t(lang, "deepdive.modules.eyebrow")}</span>
          </div>
          <h2 className="reveal reveal-delay-1 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#FDF5EB] tracking-tight leading-tight mb-3" data-testid="modules-heading">{t(lang, "deepdive.modules.heading")}</h2>
          <p className="reveal reveal-delay-2 text-[15px] text-[#FDF5EB]/65 leading-relaxed">{t(lang, "deepdive.modules.sub")}</p>
        </div>
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-start">
          <div className="grid md:grid-cols-2 gap-5">
            {groups.map((g, i) => {
              const Icon = icons[i];
              return (
                <div key={g.title} data-testid={`module-group-${i}`} className={`reveal reveal-delay-${(i % 4) + 1} relative p-7 rounded-2xl border border-white/8 bg-white/[0.02] hover:border-[#FBD556]/40 transition-colors`}>
                  <div className="flex items-start gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${accents[i]}1a` }}>
                      <Icon size={22} style={{ color: accents[i] }} strokeWidth={2} />
                    </div>
                    <div>
                      <div className="text-[10px] font-bold tracking-[0.22em] uppercase mb-1" style={{ color: accents[i] }}>Module Group {String(i + 1).padStart(2, "0")}</div>
                      <h3 className="text-xl font-bold text-[#FDF5EB] tracking-tight leading-tight">{g.title}</h3>
                    </div>
                  </div>
                  <ul className="space-y-2.5">
                    {g.items.map((it) => (
                      <li key={it} className="flex items-start gap-2.5">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: accents[i] }} />
                        <span className="text-sm text-[#FDF5EB]/75 leading-relaxed">{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
          <div className="lg:sticky lg:top-28">
            <ModulesPhone lang={lang} />
          </div>
        </div>
      </div>
    </section>
  );
};

const CapabilitiesBanner = ({ lang }) => {
  const items = t(lang, "deepdive.capabilities.items");

  return (
    <section data-testid="section-capabilities" className="border-y-[1.5px] border-[#242220]/80 bg-[#171717] py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="reveal text-xs font-bold tracking-[0.22em] uppercase text-[#A1E1B9] mb-2 text-center">{t(lang, "deepdive.capabilities.eyebrow")}</div>
        <h3 className="reveal reveal-delay-1 text-center text-2xl sm:text-3xl font-bold text-[#FDF5EB] tracking-tight mb-10">{t(lang, "deepdive.capabilities.heading")}</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {items.map((c, i) => {
            const IconMap = [WifiOff, Bell, MapPin, Languages, Fingerprint, Building2, Trees, Stamp, BookOpen];
            const Icon = IconMap[i];
            return (
              <div key={c.label} data-testid={`capability-${c.label.toLowerCase().replace(/\s+/g, "-")}`} className={`reveal reveal-delay-${(i % 4) + 1} text-center p-5 rounded-2xl border border-white/8 bg-[#1E1E1E]/50 hover:border-[#A1E1B9]/40 transition-colors`}>
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-[#A1E1B9]/12 flex items-center justify-center">
                  <Icon size={18} className="text-[#A1E1B9]" strokeWidth={2} />
                </div>
                <div className="text-sm font-bold text-[#FDF5EB] mb-0.5 leading-tight">{c.label}</div>
                <div className="text-[11px] text-[#FDF5EB]/50 leading-snug">{c.sub}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Intro = ({ lang }) => (
  <section data-testid="deepdive-intro" className="border-t-[1.5px] border-[#242220]/80 py-20 lg:py-24">
    <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
      <div className="reveal inline-flex items-center gap-2 mb-5">
        <span className="h-px w-8 bg-[#FBD556]" />
        <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#FBD556]">{t(lang, "deepdive.intro.eyebrow")}</span>
        <span className="h-px w-8 bg-[#FBD556]" />
      </div>
      <h2 className="reveal reveal-delay-1 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#FDF5EB] tracking-tight leading-tight mb-5" data-testid="deepdive-heading">{t(lang, "deepdive.intro.heading")}</h2>
      <p className="reveal reveal-delay-2 text-base sm:text-lg text-[#FDF5EB]/65 leading-relaxed max-w-2xl mx-auto" data-testid="deepdive-sub">{t(lang, "deepdive.intro.sub")}</p>
      <div className="reveal reveal-delay-3 mt-9 flex flex-wrap items-center justify-center gap-3" data-testid="deepdive-ctas">
        <a href="#contact" data-testid="deepdive-cta-download" className="inline-flex items-center gap-2 bg-[#FBD556] text-[#242220] font-bold text-sm px-5 py-3 rounded-full hover:bg-[#FFE07A] transition-all">
          <Download size={16} strokeWidth={2.4} />
          {t(lang, "deepdive.intro.download")}
        </a>
        <a href="#how-it-works" data-testid="deepdive-cta-learn" className="inline-flex items-center gap-2 text-[#FDF5EB] font-semibold text-sm px-5 py-3 rounded-full border border-[#FDF5EB]/20 hover:border-[#FBD556]/60 hover:bg-white/5 transition-all">
          {t(lang, "deepdive.intro.learnMore")}
          <ArrowRight size={16} strokeWidth={2.4} />
        </a>
      </div>
    </div>
  </section>
);

const DeepDive = ({ lang }) => {
  const deepKeys = ["rangerBook", "sharedService", "sos", "gis", "legalDocuments", "communication"];
  const illustrations = [RangerBookIllustration, SharedServiceIllustration, SOSIllustration, GISIllustration, LegalDocumentsIllustration, CommunicationIllustration];

  return (
    <div data-testid="section-deepdive" className="bg-[#1E1E1E]">
      <Intro lang={lang} />
      {deepKeys.map((key, i) => (
        <DeepRow
          key={key}
          feature={{
            id: key.replace(/([A-Z])/g, "-$1").toLowerCase(),
            ...t(lang, `deepdive.${key}`),
            Illustration: illustrations[i],
          }}
          index={i}
        />
      ))}
      <ModuleGrid lang={lang} />
      <CapabilitiesBanner lang={lang} />
    </div>
  );
};

export default DeepDive;
